import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  APPOINTMENTS_CHECKED_IN_QUERY,
  VETERINARIAN_USERS_QUERY,
} from '@/modules/appointments/appointments.gql';
import { Badge } from '@/shared/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';

interface AppointmentArrival {
  id: string;
  folio_number: string;
  sale_order_id: string | null;
  pet_name: string | null;
  veterinarian_id: string;
  scheduled_start_at: string;
}

interface AppointmentsCheckedInResponse {
  appointmentsCheckedIn: AppointmentArrival[];
}

interface VeterinarianUsersResponse {
  veterinarianUsers: { id: string; full_name: string }[];
}

interface AppointmentArrivalsModalProps {
  on_close: () => void;
  can_switch: boolean;
  on_select: (sale_order_id: string) => void;
}

/** Single-station equivalent of `CashierPosPage.tsx`'s "Citas" panel — same query, moved
 *  behind a button. Each checked-in appointment already holds its auto-created
 *  `created_by_cashier` sale order (`sale_order_id`). */
export function AppointmentArrivalsModal({
  on_close,
  can_switch,
  on_select,
}: AppointmentArrivalsModalProps): JSX.Element {
  const { t } = useTranslation();
  const { data, loading } = useQuery<AppointmentsCheckedInResponse>(APPOINTMENTS_CHECKED_IN_QUERY, {
    variables: { branch_id: null },
    fetchPolicy: 'cache-and-network',
  });
  const { data: vets_data } = useQuery<VeterinarianUsersResponse>(VETERINARIAN_USERS_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const arrivals = (data?.appointmentsCheckedIn ?? []).filter((a) => a.sale_order_id);
  const vetName = (id: string): string =>
    vets_data?.veterinarianUsers.find((v) => v.id === id)?.full_name ?? '—';

  return (
    <Dialog open onOpenChange={(open) => !open && on_close()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('single_station_pos.appointments_modal_title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          {loading && arrivals.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
          ) : null}
          {!loading && arrivals.length === 0 ? (
            <p className="text-muted-foreground py-4 text-center text-sm">
              {t('single_station_pos.appointments_empty')}
            </p>
          ) : null}
          {arrivals.map((arrival) => (
            <Card
              key={arrival.id}
              role="button"
              tabIndex={can_switch ? 0 : -1}
              onClick={() => can_switch && arrival.sale_order_id && on_select(arrival.sale_order_id)}
              onKeyDown={(event) => {
                if (can_switch && arrival.sale_order_id && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  on_select(arrival.sale_order_id);
                }
              }}
              className={`${
                can_switch ? 'cursor-pointer hover:border-primary/50' : 'cursor-not-allowed opacity-60'
              } border border-border`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-sm">{arrival.pet_name ?? '-'}</CardTitle>
                  <Badge variant="muted">{arrival.folio_number}</Badge>
                </div>
              </CardHeader>
              <CardContent className="text-muted-foreground text-xs">
                {vetName(arrival.veterinarian_id)}
              </CardContent>
            </Card>
          ))}
          {!can_switch ? (
            <p className="text-muted-foreground text-xs">
              {t('single_station_pos.cannot_switch_hint')}
            </p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
